import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Plus, Check, Circle, Sprout, Trash2, Lightbulb, Timer } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/store/AppContext";
import { QuickCapture } from "@/components/QuickCapture";
import { greeting, prettyToday, currentHour } from "@/lib/date";

const inputBase =
  "w-full bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-forest-500/40";

const Today: React.FC = () => {
  const {
    data,
    ensureToday,
    setDailyAt,
    toggleDailyAt,
    setBlockTitleAt,
    setBlockTimeAt,
    toggleBlockAt,
    removeCapture,
    captureToTask,
  } = useApp();
  const navigate = useNavigate();
  const [capture, setCapture] = useState(false);

  useEffect(() => {
    ensureToday();
  }, [ensureToday]);

  const weekly = data.weekly.items;
  const daily = data.daily.items;
  const blocks = data.flow.blocks;

  const currentIdx = useMemo(() => {
    const h = currentHour();
    let idx = -1;
    blocks.forEach((b, i) => {
      if (parseInt(b.time, 10) <= h) idx = i;
    });
    return idx;
  }, [blocks]);

  const openFocus = (minutes: number, label: string | null) => {
    navigate("/focus", { state: { minutes, label } });
  };

  return (
    <div>
      {/* 1 · Greeting */}
      <header className="flex items-start justify-between gap-4 mb-14">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-forest-500 mb-2">
            {prettyToday()}
          </p>
          <h1 className="font-heading text-4xl font-light tracking-tight text-forest-900 leading-tight">
            {greeting()}
            {data.userName ? `,` : "."}
            {data.userName && (
              <>
                <br />
                <span className="text-forest-800">{data.userName}.</span>
              </>
            )}
          </h1>
        </div>
        <button
          onClick={() => setCapture(true)}
          data-testid="today-quick-capture"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-peach-light text-peach-deep px-4 py-2.5 text-sm font-medium hover:bg-peach/60 transition-colors"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Idée
        </button>
      </header>

      {/* 2 · Weekly orientation (read-only, set in Friday Reset) */}
      <section className="mb-14">
        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-forest-500">Cette semaine</p>
          <p className="text-sm text-forest-500 mt-1">Ton cap, défini au Reset du vendredi.</p>
        </div>
        {weekly.some((w) => w?.text?.trim()) ? (
          <div className="bg-warmwhite rounded-3xl border border-forest-900/5 shadow-[0_8px_32px_rgba(33,38,33,0.04)] px-6 py-2" data-testid="weekly-orientations">
            {weekly
              .filter((w) => w?.text?.trim())
              .map((w, i, arr) => (
                <div
                  key={w.id}
                  className={`flex items-center gap-4 py-4 ${i < arr.length - 1 ? "border-b border-forest-900/5" : ""}`}
                  data-testid={`weekly-orientation-${i}`}
                >
                  <Sprout strokeWidth={1.5} className="h-5 w-5 text-sage shrink-0" />
                  <p className="text-forest-800 text-[15px] leading-relaxed">{w.text}</p>
                </div>
              ))}
          </div>
        ) : (
          <button
            onClick={() => navigate("/reset")}
            data-testid="weekly-empty-cta"
            className="w-full text-left bg-warmwhite rounded-3xl border border-dashed border-forest-900/15 px-6 py-6 flex items-center gap-4 hover:border-sage/50 transition-colors"
          >
            <Sprout strokeWidth={1.5} className="h-6 w-6 text-forest-500/50 shrink-0" />
            <span className="text-sm text-forest-500 leading-relaxed">
              Tes 3 orientations se définissent lors du <span className="text-sage-dark font-medium">Reset du vendredi</span>. Tape ici pour l'ouvrir.
            </span>
          </button>
        )}
      </section>

      {/* 3 · Today's priorities */}
      <section className="mb-14">
        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-forest-500">Aujourd'hui</p>
          <p className="text-sm text-forest-500 mt-1">Trois intentions, sans pression.</p>
        </div>
        <div className="bg-warmwhite rounded-3xl border border-forest-900/5 shadow-[0_8px_32px_rgba(33,38,33,0.04)] px-6 py-2">
          {[0, 1, 2].map((i) => {
            const done = daily[i]?.done ?? false;
            return (
              <div
                key={i}
                className={`flex items-center gap-4 py-4 ${i < 2 ? "border-b border-forest-900/5" : ""}`}
              >
                <button
                  onClick={() => toggleDailyAt(i)}
                  data-testid={`daily-toggle-${i}`}
                  aria-label="Marquer la priorité"
                  className="shrink-0"
                >
                  {done ? (
                    <span className="h-6 w-6 rounded-full bg-sage flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" strokeWidth={2.5} />
                    </span>
                  ) : (
                    <Circle strokeWidth={1.5} className="h-6 w-6 text-forest-500/40" />
                  )}
                </button>
                <input
                  value={daily[i]?.text ?? ""}
                  onChange={(e) => setDailyAt(i, e.target.value)}
                  data-testid={`daily-input-${i}`}
                  placeholder={`Priorité ${i + 1}…`}
                  className={`${inputBase} text-[15px] leading-relaxed ${
                    done ? "text-forest-500 line-through" : "text-forest-900"
                  }`}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* 5 · Focus entry point (50 min) */}
      <section className="mb-14">
        <button
          onClick={() => openFocus(50, "Focus profond")}
          data-testid="focus-entry"
          className="w-full text-left bg-warmwhite rounded-3xl border border-forest-900/5 shadow-[0_8px_32px_rgba(33,38,33,0.04)] p-6 flex items-center gap-5 hover:shadow-[0_12px_40px_rgba(33,38,33,0.07)] transition-shadow"
        >
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-forest-500 mb-2">
              <Timer strokeWidth={1.75} className="h-3.5 w-3.5" /> Focus profond
            </p>
            <p className="font-heading text-2xl font-light tracking-tight text-forest-900">
              50 minutes de calme
            </p>
            <p className="text-sm text-forest-500 mt-1">Une seule chose, pleinement.</p>
          </div>
          <span className="h-16 w-16 rounded-full bg-sage text-white flex items-center justify-center shadow-[0_8px_24px_rgba(138,154,134,0.4)] shrink-0">
            <Play className="h-6 w-6 fill-current ml-0.5" />
          </span>
        </button>
      </section>

      {/* 4 · Daily flow — 7 time blocks */}
      <section className="mb-14">
        <div className="mb-5">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-forest-500">Le fil de la journée</p>
          <p className="text-sm text-forest-500 mt-1">Le rythme de tes sept moments.</p>
        </div>

        <div className="relative pl-7">
          <div className="absolute left-[9px] top-3 bottom-3 w-px bg-forest-900/10" />
          <div className="space-y-2.5">
            {blocks.length === 7
              ? blocks.map((b, i) => {
                  const isNow = i === currentIdx;
                  return (
                    <div key={b.id} className="relative" data-testid={`flow-block-${i}`}>
                      <button
                        onClick={() => toggleBlockAt(i)}
                        data-testid={`flow-toggle-${i}`}
                        aria-label="Marquer le moment"
                        className="absolute -left-7 top-4 z-10 h-[18px] w-[18px] flex items-center justify-center"
                      >
                        {b.done ? (
                          <span className="h-[18px] w-[18px] rounded-full bg-sage flex items-center justify-center ring-4 ring-cream">
                            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                          </span>
                        ) : (
                          <span
                            className={`block h-[18px] w-[18px] rounded-full ring-4 ring-cream ${
                              isNow ? "bg-peach-deep" : "bg-forest-900/15"
                            }`}
                          />
                        )}
                      </button>

                      <div
                        className={`rounded-2xl px-4 py-3 flex items-center gap-3 transition-colors ${
                          isNow
                            ? "bg-peach-light border border-peach/50"
                            : "bg-warmwhite border border-forest-900/5"
                        }`}
                      >
                        <div className="w-[58px] shrink-0">
                          <input
                            type="time"
                            value={b.time}
                            onChange={(e) => setBlockTimeAt(i, e.target.value)}
                            data-testid={`flow-time-${i}`}
                            aria-label="Choisir l'heure du bloc"
                            className="w-full bg-transparent border-0 p-0 text-sm font-semibold text-forest-800 tabular-nums focus:outline-none cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:w-0"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] uppercase tracking-[0.12em] text-forest-500">
                            {b.label}
                          </p>
                          <input
                            value={b.title}
                            onChange={(e) => setBlockTitleAt(i, e.target.value)}
                            data-testid={`flow-input-${i}`}
                            placeholder="Que veux-tu y vivre ?"
                            className={`${inputBase} text-[15px] mt-0.5 ${
                              b.done ? "text-forest-500 line-through" : "text-forest-900"
                            }`}
                          />
                        </div>
                        <button
                          onClick={() => openFocus(50, b.title.trim() || b.label)}
                          data-testid={`flow-focus-${i}`}
                          aria-label="Démarrer un focus"
                          className="h-9 w-9 rounded-full bg-sage/12 text-sage-dark flex items-center justify-center shrink-0 hover:bg-sage/20 transition-colors"
                        >
                          <Play className="h-3.5 w-3.5 fill-current" />
                        </button>
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      </section>

      {/* 7 · Ideas to review */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb strokeWidth={1.75} className="h-4 w-4 text-peach-deep" />
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-forest-500">
            Idées à revoir
          </p>
          {data.captures.length > 0 && (
            <span className="text-[11px] font-semibold text-peach-deep bg-peach-light rounded-full px-2 py-0.5">
              {data.captures.length}
            </span>
          )}
        </div>

        {data.captures.length === 0 ? (
          <div className="bg-warmwhite rounded-3xl border border-forest-900/5 px-6 py-8 text-center">
            <p className="text-sm text-forest-500 leading-relaxed">
              Rien à trier. Capture une idée quand elle passe, tu la retrouveras ici.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.captures.map((c) => (
              <div
                key={c.id}
                className="bg-warmwhite rounded-3xl border border-forest-900/5 shadow-[0_8px_32px_rgba(33,38,33,0.04)] p-4"
                data-testid={`capture-${c.id}`}
              >
                <p className="text-forest-800 leading-relaxed mb-3">{c.text}</p>
                <div className="flex items-center gap-2">
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      const v = e.target.value;
                      captureToTask(c.id, v === "__none__" ? null : v || null);
                      toast.success("Transformé en tâche.");
                    }}
                    data-testid={`capture-to-task-${c.id}`}
                    className="flex-1 bg-cream-surface border border-forest-900/5 rounded-xl px-3 py-2.5 text-sm text-forest-700 focus:outline-none focus:ring-2 focus:ring-sage/40"
                  >
                    <option value="" disabled>
                      → Transformer en tâche…
                    </option>
                    <option value="__none__">Sans projet</option>
                    {data.projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeCapture(c.id)}
                    data-testid={`delete-capture-${c.id}`}
                    aria-label="Supprimer"
                    className="p-2.5 rounded-xl text-forest-500/70 hover:text-blush hover:bg-forest-900/5"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <QuickCapture open={capture} onClose={() => setCapture(false)} />
    </div>
  );
};

export default Today;
